import React from 'react';
import { Icon } from '@salecycle/ui-component-library';
import { RcsCardContent, RcsCardButton } from '../../types/rcsTemplateData';
import { normalizeRcsPreviewActionUrl } from '../../utils/normalizeRcsPreviewActionUrl';
import {
  CardBubble,
  CardMediaWrapper,
  CardImage,
  CardMediaPlaceholder,
  CardContent,
  CardTitle,
  CardTitlePlaceholder,
  CardText,
  CardTextPlaceholder,
  CardButtons,
  CardButton,
  CardButtonLink,
  CardButtonIcon,
} from './RichCard.styles';

/**
 * Returns the icon name for a card button based on dial/openUrl, or null if none.
 */
function getButtonIcon(button: RcsCardButton): string | null {
  if (button.dial) return 'phone';
  if (button.openUrl) return 'globe';
  return null;
}

function getCompiledCtaLink(button: RcsCardButton): { href: string; title: string; external: boolean } | null {
  const rawOpenUrl = button.openUrl?.url?.trim();
  if (rawOpenUrl) {
    const url = normalizeRcsPreviewActionUrl(rawOpenUrl);
    return { href: url, title: url, external: /^https?:\/\//i.test(url) };
  }
  const rawPhone = button.dial?.phoneNumber?.trim();
  if (rawPhone) {
    const digits = rawPhone.replace(/\s/g, '');
    const href = rawPhone.toLowerCase().startsWith('tel:') ? rawPhone : `tel:${digits}`;
    return { href, title: rawPhone, external: false };
  }
  return null;
}

function renderCardButtonContent(button: RcsCardButton, index: number) {
  const iconName = getButtonIcon(button);
  const inner = (
    <>
      {iconName && (
        <CardButtonIcon>
          <Icon icon={iconName} size="extra-small" color="inherit" />
        </CardButtonIcon>
      )}
      <span>{button.caption || ' '}</span>
    </>
  );

  const link = getCompiledCtaLink(button);
  if (link) {
    return (
      <CardButtonLink
        key={`${button.caption}-${index}`}
        data-testid="RichCardButton"
        href={link.href}
        title={link.title}
        {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {inner}
      </CardButtonLink>
    );
  }

  return (
    <CardButton key={`${button.caption}-${index}`} data-testid="RichCardButton">
      {inner}
    </CardButton>
  );
}

export interface RichCardProps {
  card: RcsCardContent;
  mediaPlaceholderText?: string;
  titlePlaceholder?: string;
  descriptionPlaceholder?: string;
  /** Accessible label for the card image; falls back to card title when not provided. */
  mediaAltText?: string;
  'data-testid'?: string;
}

/**
 * Renders a Link Mobility RCS card (media, title, description, CTAs) for preview.
 * Placeholder props should be passed from the parent using t() for i18n.
 */
export const RichCard: React.FC<RichCardProps> = ({
  card,
  mediaPlaceholderText = '',
  titlePlaceholder = '',
  descriptionPlaceholder = '',
  mediaAltText,
  'data-testid': dataTestId,
}) => {
  const hasMedia = Boolean(card.media?.source?.trim());
  const hasTitle = Boolean(card.title?.trim());
  const hasText = Boolean(card.text?.trim());
  const hasButtons = Array.isArray(card.buttons) && card.buttons.length > 0;
  const imageAlt = mediaAltText ?? card.title ?? '';

  return (
    <CardBubble data-testid={dataTestId ?? 'RichCard'}>
      <CardMediaWrapper>
        {hasMedia ? (
          <CardImage src={card.media!.source} alt={imageAlt} />
        ) : (
          <CardMediaPlaceholder data-testid="RichCardMediaPlaceholder">{mediaPlaceholderText}</CardMediaPlaceholder>
        )}
      </CardMediaWrapper>
      <CardContent>
        {hasTitle ? (
          <CardTitle data-testid="RichCardTitle">{card.title}</CardTitle>
        ) : (
          <CardTitlePlaceholder data-testid="RichCardTitlePlaceholder">{titlePlaceholder}</CardTitlePlaceholder>
        )}
        {hasText ? (
          <CardText data-testid="RichCardText">{card.text}</CardText>
        ) : (
          <CardTextPlaceholder data-testid="RichCardDescriptionPlaceholder">
            {descriptionPlaceholder}
          </CardTextPlaceholder>
        )}
        {hasButtons && (
          <CardButtons data-testid="RichCardButtons">
            {card.buttons!.map((button, index) => renderCardButtonContent(button, index))}
          </CardButtons>
        )}
      </CardContent>
    </CardBubble>
  );
};
